import { PageKey } from "@apis:ui_branding/ui_branding";
import { Box, TextField } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PageConfigCard } from "./PageConfigCard";
import { PageTabs } from "./PageTabs";
import { SectionWrapper } from "./SectionWrapper";
import { FieldLabel } from "./shared-styles";
import { getPageLabel } from "./utils";

interface TextSectionProps {
  activePage: PageKey;
  onPageChange: (page: PageKey) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
}

// Pages that only show title field (no description)
const TITLE_ONLY_PAGES: PageKey[] = ["chat", "analytics"];

export const TextSection = ({
  activePage,
  onPageChange,
  control,
}: TextSectionProps) => {
  const { t } = useTranslation();
  const showDescriptionField = !TITLE_ONLY_PAGES.includes(activePage);
  const titleLabel =
    activePage === "chat"
      ? t("preferences.uiBranding.greeting")
      : t("preferences.uiBranding.pageTitle");

  return (
    <SectionWrapper instruction={t("preferences.uiBranding.instructions.text")}>
      <PageTabs activePage={activePage} onPageChange={onPageChange} />
      <PageConfigCard
        title={t("preferences.uiBranding.pageContentTitle", {
          page: getPageLabel(activePage, t),
        })}
      >
        <Box sx={{ mb: showDescriptionField ? 1.5 : 0 }}>
          <FieldLabel>{titleLabel}</FieldLabel>
          <Controller
            key={`title-${activePage}`}
            name={`pages.${activePage}.title`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || null)}
                fullWidth
                size="small"
                placeholder={t(
                  `preferences.uiBranding.pageDefaults.${activePage}.title`,
                )}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "common.white" },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    px: 1.25,
                    fontSize: "0.8rem",
                  },
                }}
              />
            )}
          />
        </Box>

        {showDescriptionField && (
          <Box>
            <FieldLabel>{t("common.description")}</FieldLabel>
            <Controller
              key={`description-${activePage}`}
              name={`pages.${activePage}.description`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  placeholder={t(
                    `preferences.uiBranding.pageDefaults.${activePage}.description`,
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "common.white",
                      p: 0,
                    },
                    "& .MuiOutlinedInput-input": {
                      py: 1,
                      px: 1.25,
                      fontSize: "0.8rem",
                    },
                  }}
                />
              )}
            />
          </Box>
        )}
      </PageConfigCard>
    </SectionWrapper>
  );
};
